import { RfqTemplate } from '@lune-fe/dapi-models'
import { ButtonGroupNew, ButtonNew, IconButton, Modal, TableNew, Text } from '@lune-fe/lune-ui-lib'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { deleteRfqTemplate, downloadDataSheet } from 'endpoints/dapi'
import useMappings from 'hooks/useMappings'
import {
    generateWorkingData,
    parseDatasheet,
} from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import { setRfqState } from 'views/CalculateEmissions/RFQ/utils/rfqStateStore'
import { RowNumber } from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import { RFQTemplateState } from 'views/CalculateEmissions/RFQ/utils/types'
import { getTableDimensions } from 'views/CalculateEmissions/RFQ/utils/utils'

type SheetType = RfqTemplate['sheetType']

const ManageMappings: FC = () => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [sheetType, setSheetType] = useState<SheetType>('logistics_user_template')
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
    const { mappings, loading, refetch } = useMappings(sheetType)
    const { enqueueSnackbar: snackbar } = useSnackbar()
    const navigate = useNavigate()

    const routePath =
        sheetType === 'rfq'
            ? '/calculate-emissions/tender'
            : '/calculate-emissions/logistics-sheets'

    return (
        <>
            <ButtonNew variant={'secondary'} onClick={() => setIsModalOpen(true)}>
                Manage Mappings
            </ButtonNew>
            <Modal
                maxWidth={'md'}
                fullWidth
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                header={
                    <Text variant={'h5'} sx={{ mb: 1 }}>
                        Manage mappings
                    </Text>
                }
                subheader={
                    <ButtonGroupNew
                        items={[
                            {
                                label: 'CSV',
                                value: 'logistics_user_template',
                            },
                            {
                                label: 'Tender',
                                value: 'rfq',
                            },
                        ]}
                        onChange={(type) => {
                            setSheetType(type as SheetType)
                        }}
                        value={sheetType}
                    />
                }
            >
                <Box
                    sx={{
                        width: '100%',
                        maxHeight: '350px',
                    }}
                >
                    <TableNew.TableContainer>
                        <TableNew
                            stickyHeader
                            loading={loading}
                            empty={!loading && !mappings.length}
                            headerCells={
                                <>
                                    <TableNew.TableCell colSize={4}>Name</TableNew.TableCell>
                                    <TableNew.TableCell colSize={3}>Created by</TableNew.TableCell>
                                    <TableNew.TableCell colSize={3}>Created on</TableNew.TableCell>
                                    <TableNew.TableCell colSize={2} />
                                </>
                            }
                            bodyCells={
                                <>
                                    {mappings.map((item, index) => (
                                        <TableNew.TableRow key={index}>
                                            <TableNew.TableCell colSize={4}>
                                                {item.name}
                                            </TableNew.TableCell>
                                            <TableNew.TableCell colSize={3}>
                                                {item.createdBy}
                                            </TableNew.TableCell>
                                            <TableNew.TableCell colSize={3}>
                                                {moment(item.createdAt).format('MMMM DD, YYYY')}
                                            </TableNew.TableCell>
                                            <TableNew.TableCell colSize={2} alignEnd>
                                                {item.dataSheetId !== null && (
                                                    <IconButton
                                                        onClick={async () => {
                                                            const data = await downloadDataSheet(
                                                                item.dataSheetId!,
                                                                item.dataSheetAccountId,
                                                            )
                                                            const parsedData = await parseDatasheet(
                                                                sheetType,
                                                                data,
                                                                item.template.sheetName,
                                                                RowNumber.fromOneBased(
                                                                    item.template.headerRow,
                                                                ),
                                                            )
                                                            const {
                                                                headerRowNumber,
                                                                nrColumns,
                                                                headerStartIndex,
                                                                endRow,
                                                            } = getTableDimensions(
                                                                parsedData.sheet,
                                                                RowNumber.fromOneBased(
                                                                    item.template.headerRow,
                                                                ),
                                                            )
                                                            const templateData: RFQTemplateState = {
                                                                ...item.template,
                                                                ...generateWorkingData(
                                                                    parsedData.sheet,
                                                                    headerRowNumber,
                                                                    nrColumns,
                                                                ),
                                                                ...parsedData,
                                                                endRow: endRow.toZeroBased(),
                                                                headerStartIndex,
                                                                numberOfLegs:
                                                                    item.template.legs.length,
                                                                load: item.template.load,
                                                                loadSize: item.template.loadSize,
                                                                shippedAt: item.template.shippedAt,
                                                                shipmentId:
                                                                    item.template.shipmentId,
                                                                clientAccountName:
                                                                    item.template.clientAccountName,
                                                                clientAccountCurrency:
                                                                    item.template
                                                                        .clientAccountCurrency,
                                                                currentLeg: 1,
                                                                legs: item.template.legs,
                                                                rfqTemplate: {
                                                                    id: item.id,
                                                                    name: item.name,
                                                                },
                                                                autoFillState: { legs: [] },
                                                            }
                                                            const stateKey = `rfq-state-${Date.now()}-${Math.random()}`
                                                            setRfqState(stateKey, templateData)
                                                            navigate(routePath, {
                                                                state: {
                                                                    initialStage: item.template
                                                                        .clientAccountName
                                                                        ? 'clientAccountDetails'
                                                                        : 'shippingDetails',
                                                                    stateKey,
                                                                    from: 'imports',
                                                                },
                                                            })
                                                            setIsModalOpen(false)
                                                        }}
                                                        style={'floating'}
                                                        size={'sm'}
                                                        leadingIcon={<EditIcon />}
                                                    />
                                                )}
                                                <IconButton
                                                    sx={{ ml: 2 }}
                                                    onClick={() =>
                                                        setDeleteTarget({
                                                            id: item.id,
                                                            name: item.name,
                                                        })
                                                    }
                                                    style={'floating'}
                                                    size={'sm'}
                                                    leadingIcon={<DeleteIcon />}
                                                />
                                            </TableNew.TableCell>
                                        </TableNew.TableRow>
                                    ))}
                                </>
                            }
                        />
                    </TableNew.TableContainer>
                </Box>
            </Modal>
            {deleteTarget && (
                <Modal
                    sx={{
                        '.MuiPaper-root': {
                            p: 3,
                            borderRadius: '32px',
                            boxShadow: 16,
                        },
                    }}
                    withoutSpacing
                    maxWidth={'xs'}
                    open={true}
                    onClose={() => setDeleteTarget(null)}
                    header={
                        <Text variant={'h5'} sx={{ mb: 1 }}>
                            Delete mapping
                        </Text>
                    }
                    actions={
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                            <ButtonNew variant={'secondary'} onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </ButtonNew>
                            <ButtonNew
                                onClick={async () => {
                                    await deleteRfqTemplate(deleteTarget.id)
                                    snackbar('Mapping deleted successfully')
                                    refetch()
                                    setDeleteTarget(null)
                                }}
                            >
                                Delete
                            </ButtonNew>
                        </Box>
                    }
                >
                    <Text variant="body2">
                        Are you sure you want to delete &quot;{deleteTarget.name}&quot;? This action
                        cannot be undone.
                    </Text>
                </Modal>
            )}
        </>
    )
}

export default ManageMappings
